import { useState, useMemo, ReactNode, useRef, useEffect, useLayoutEffect, useCallback, RefObject, useDeferredValue } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
  FilterFn,
  getFilteredRowModel,
  Row,
} from '@tanstack/react-table';
import { defaultRangeExtractor, useVirtualizer, useWindowVirtualizer } from '@tanstack/react-virtual';
import Skeleton from '@mui/material/Skeleton';
import ExportTableBtn, { ExportFormat, ExportMenuItem } from './ExportTableBtn';
import type { PdfSection } from '@/helpers/exportPdfHelper';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import { CardList } from '@/components/ui/CardList/CardList';
import emptyState from '@/assets/icons/empty-state.svg';
import {
  densityCssVars,
  LEGACY_TABLE_DENSITY,
  MOBILE_ROW_HEIGHT_PX,
  MOBILE_DENSITY_ROW_HEIGHT_PX,
  MOBILE_TRANSACTION_ROW_HEIGHT_PX,
  phoneTransactionBoardCssVars,
  TABLE_DENSITY_TOKENS,
  TABLE_DENSITY_TWO_LINE_ROW_H,
  TableDensity,
} from './density';
import useColumnLayout from './useColumnLayout';
import { declaredMinWidthPx, fitColumnWidths, isFlexibleColumnClass, FitColumn } from './fitColumnWidths';
import ViewOptionsMenu from './ViewOptionsMenu';
import useCalendarMode from '@/hooks/useCalendarMode';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import styles from './DataTable.module.scss';

/** Narrower than this a column shows nothing but an ellipsis. */
const MIN_COLUMN_WIDTH_PX = 56;

// A width the user set has to beat the utility classes the board declares, and
// table-layout:fixed only honours it when all three properties agree.
const columnWidthStyle = (width?: number, minLayoutWidthPx?: number): React.CSSProperties | undefined => {
  if (width) {
    const effective = minLayoutWidthPx ? Math.max(width, minLayoutWidthPx) : width;
    return { width: `${effective}px`, minWidth: `${effective}px`, maxWidth: `${effective}px` };
  }
  if (minLayoutWidthPx) {
    return { minWidth: `${minLayoutWidthPx}px` };
  }
  return undefined;
};

const TOOLBAR_GAP_PX = 16;
const CROWDED_HYSTERESIS_PX = 24;

/**
 * Width the node wants when it is not squeezed by the current grid. offsetWidth
 * of an already-shrunk search field under-reports, so crowded never fires and
 * the export button is left floating in the empty title-row gap.
 */
const maxContentWidth = (el: HTMLElement): number => {
  const previous = el.style.width;
  el.style.width = 'max-content';
  const width = Math.max(el.scrollWidth, el.offsetWidth);
  el.style.width = previous;
  return width;
};

/** Width a flex row would take if its children sat on one line. */
const intrinsicFlexWidth = (el: HTMLElement): number => {
  const style = getComputedStyle(el);
  const children = Array.from(el.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement && (child.offsetWidth > 0 || child.scrollWidth > 0)
  );
  if (children.length === 0) return maxContentWidth(el);

  const isColumn = style.flexDirection === 'column' || style.flexDirection === 'column-reverse';
  if (isColumn) {
    return Math.max(maxContentWidth(el), ...children.map((child) => intrinsicFlexWidth(child)));
  }
  if (children.length === 1) return intrinsicFlexWidth(children[0]);

  const gap = parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
  return children.reduce((sum, child) => sum + maxContentWidth(child), 0) + gap * (children.length - 1);
};

const filtersRowWidth = (slot: HTMLElement): number => {
  const marked = slot.querySelector('[data-board-filters]');
  const chips = marked instanceof HTMLElement ? intrinsicFlexWidth(marked) : 0;
  // The slot also holds summary cards and the "add" button; those share the
  // row with the toolbar, so counting only the chips left Supporteds uncrowded.
  return Math.max(chips, maxContentWidth(slot));
};

const isOverflowing = (el: HTMLElement): boolean => el.scrollWidth > el.clientWidth + 1;

/**
 * True when the filter chips and the search/density cluster cannot share a row.
 * Opening the sidebar is the usual reason the board suddenly has no room.
 */
const useCrowdedToolbar = (
  enabled: boolean,
  chromeRef: RefObject<HTMLElement | null>,
  filtersRef: RefObject<HTMLElement | null>,
  toolbarRef: RefObject<HTMLElement | null>
): boolean => {
  const [crowded, setCrowded] = useState(false);

  useLayoutEffect(() => {
    if (!enabled) {
      setCrowded(false);
      return;
    }

    const chrome = chromeRef.current;
    const filters = filtersRef.current;
    const toolbar = toolbarRef.current;
    if (!chrome || !filters || !toolbar) return;

    let frame = 0;

    const measure = () => {
      frame = 0;
      const needed = filtersRowWidth(filters) + maxContentWidth(toolbar) + TOOLBAR_GAP_PX;
      const available = chrome.clientWidth;
      const overflowing = isOverflowing(toolbar) || isOverflowing(filters);
      setCrowded((wasCrowded) => {
        if (overflowing) return true;
        return wasCrowded ? needed + CROWDED_HYSTERESIS_PX > available : needed > available;
      });
    };

    // Do NOT disconnect and re-observe from inside the callback: observe()
    // always queues a fresh notification for its target, so re-observing here
    // leaves undelivered notifications on every pass - an endless loop that
    // fired "ResizeObserver loop completed with undelivered notifications" once
    // per frame on every board. Measuring in a frame of its own also keeps the
    // transient width writes maxContentWidth makes out of the observation pass.
    const observer = new ResizeObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    });
    observer.observe(chrome);
    observer.observe(filters);
    observer.observe(toolbar);
    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [enabled, chromeRef, filtersRef, toolbarRef]);

  return crowded;
};

interface SelectAction<T> {
  label: string;
  onClick: (ids: T[]) => void;
  isVisible?: (selectedRows: T[]) => boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

interface ColumnMeta {
  className?: string;
  mobileClassName?: string;
  style?: React.CSSProperties;
  hiddenOnMobile?: boolean;
  /** Columns the user may not resize or move, such as the selection checkbox. */
  lockLayout?: boolean;
  /** Floor for a pinned/saved width — keeps sidebar reflow from starving fixed labels. */
  minLayoutWidthPx?: number;
  /**
   * HAT-1364: a column that gives width back when a saved layout no longer fits the
   * screen, down to its floor (`minLayoutWidthPx`, else its `min-w-[…]`). Defaults to
   * "declared without `w-[…]`/`max-w-[…]`" - the column the table was already sizing
   * from the leftover, which must not be frozen at the width it had on a wider screen.
   */
  flexible?: boolean;
}

interface SkeletonConfig {
  widths?: string[];
  customRender?: () => ReactNode;
}

type SkeletonConfigMap = Record<string, (isEvenRow: boolean) => SkeletonConfig>;

/**
 * HAT-1203: one labelled, collapsible section of the body — «תנועות עתידיות» above
 * «תנועות שוטפות». Groups render in the order given; a row belongs to the first group
 * whose `contains` accepts it, and rows nobody claims trail behind the last group.
 * Collapsing is the page's business: a collapsed group keeps its header row but the
 * page leaves its rows out of `data`, so selection, search and "select all" cannot
 * disagree about what is on screen. `count` is what the header prints, which lets it
 * include the rows that are currently hidden.
 */
export interface RowGroup<T> {
  id: string;
  /** The group's name. The header prints the count after it. */
  label: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  contains: (row: T) => boolean;
  /** The design's small red dot after the count, shown while the group is collapsed; `flagTitle` says why on hover. */
  flagged?: boolean;
  flagTitle?: string;
  /** A short chip after the count, for what matters before the group is opened. */
  badge?: ReactNode;
  /** Some of the group's rows are still on their way: a small spinner sits by the count, and the
   *  "nothing here" line waits, because saying "none" while rows may still arrive would be a lie. */
  loading?: boolean;
  /** What the spinner means, for screen readers and hover. */
  loadingLabel?: string;
  /** One line under the header when the group has no rows at all (`count` is 0). */
  emptyMessage?: ReactNode;
  /** One line under the header when it has rows but the search left none of them. */
  noMatchMessage?: ReactNode;
  /** A muted line under the header — e.g. a source that did not load. */
  note?: ReactNode;
}

interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  /**
   * HAT-1212: מזהה יציב לשורה. בלעדיו react-table משתמש באינדקס — כך
   * כשהעמוד עושה refetch עם אותה כמות שורות במיון אחר, ה-state של
   * הבחירה נשאר על אותם אינדקסים ומצוירת בחירה על השורות הלא-נכונות.
   * מומלץ מאוד להעביר; ה-DataTable משתמש בערך שהחזרנו גם כדי לזהות
   * שינוי-סדר או החלפת-שורות ולנקות את הבחירה במקרה כזה.
   */
  getRowId?: (row: T, index: number) => string;
  /** Splits the body into collapsible groups with a header row each. Table layouts only. */
  rowGroups?: RowGroup<T>[];
  isLoading?: boolean;
  sortBy?: { id: string; desc: boolean };
  filters?: ReactNode;
  /**
   * Search / density cluster. Sits on the filter row when there is room, and
   * jumps to the title row (next to export) when the board is too narrow —
   * typically after the sidebar opens.
   */
  toolbar?: ReactNode;
  /**
   * Actions that belong beside the title, next to export - the board's primary action, a
   * refresh control - rather than on the filter row. The filter row already holds the chips,
   * density and search; on the standing-orders board it also held these two, and read as
   * one crowded line where the transactions board reads as two calm ones.
   */
  headerActions?: ReactNode;
  /**
   * A caption for the board, under its heading: what the rows add up to, said in a line. It
   * belongs to the title rather than to a band of its own - a single line of text surrounded
   * by its own margins reads as floating, and pushes the table down for nothing.
   */
  titleCaption?: ReactNode;
  title: string;
  handleRowClick?: (row: T) => void;
  selectionActions?: SelectAction<T>[];
  selectionSummary?: (selectedItems: T[]) => ReactNode;
  activeRecord?: T;
  /** Scrollport height. When omitted, the body fills the remaining viewport. */
  tableHeight?: string;
  /** Document scrolling for full-page boards; bounded dialogs keep element scrolling. */
  scrollMode?: 'document' | 'element';
  /** A narrow date rail, name and amount, retaining the three semantic columns. */
  mobileLayout?: 'table' | 'transactions';
  minTableWidth?: number;
  customGlobalSearch: FilterFn<T>;
  exportColumns?: ColumnDef<T>[];
  exportData?: T[];
  exportFilename?: string;
  // Async provider for the exported rows - lets pages enrich rows with extra data fetched on demand
  getExportData?: () => Promise<T[]>;
  /** Called after a file was handed to the browser, with the format the user picked. */
  onExport?: (format: ExportFormat) => void;
  /**
   * HAT-1399: puts PDF next to Excel in the export button. The board hands over the rows as it
   * shows them right now - search, filters and sort applied, a collapsed group's rows left
   * out - under its group headings, and the page turns them into the document.
   */
  exportPdf?: (sections: PdfSection<T>[]) => Promise<void>;
  /** HAT-1493: entries after the file formats in the export menu, that open something on the screen. */
  exportExtraItems?: ExportMenuItem[];
  skeletonConfig?: SkeletonConfigMap; // Optional: Custom skeleton configuration for columns
  searchQuery: string;
  emptyStateContent?: { title: string; description: string; action?: ReactNode };
  setSearchQuery: (value: string) => void;
  // Endless scrolling props
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadingMoreText?: string;
  // Manual sorting - when true, skip client-side sorting (data comes pre-sorted from server)
  manualSorting?: boolean;
  // Callback when sorting changes (for server-side sorting)
  onSortingChange?: (sorting: SortingState) => void;
  /** Row height / typography preset. Ignored on a phone, which has a layout of its own. */
  density?: TableDensity;
  onDensityChange?: (density: TableDensity) => void;
  /** Transactions board: לועזי|עברי beside density, both in the «תצוגה» menu (HAT-1210, HAT-1492). */
  showCalendarMode?: boolean;
  /**
   * Set when the board's cells carry a second line, so the row is given the height for it.
   * The density presets describe a one-line row; without this the sub-line spills into the
   * row beneath, because the virtualizer places rows at a fixed pitch. Boards that do not
   * pass it are unaffected.
   */
  twoLineRows?: boolean;
  /**
   * A full-width line under the board's title - a banner about the whole board, say. The
   * filters slot shares its row with the toolbar, so anything placed there is squeezed into
   * part of the width; this gets a row of its own.
   */
  notice?: ReactNode;
  /**
   * Turns on column resizing and reordering, and names the slot the arrangement
   * is saved under. Boards without a key keep the widths their code declares.
   */
  columnLayoutKey?: string;
  /** When set, the search field lives in the board toolbar — not in the filters row. */
  searchPlaceholder?: string;
  searchTooltip?: string;
  /*
   * ------------------------------------------------------------
   *  תצוגת הכרטיסים במסך צר
   * ------------------------------------------------------------
   * לוח שמספק renderCard מציג כרטיסים במקום טבלה במסך צר, לפי
   * העיצוב שהועבר מ-hatzdaka-nedarim (ר' components/ui/CardList).
   *
   * ⚠️ זה **מצב תצוגה, לא מצב נתונים**. הכרטיסים מקבלים בדיוק את
   * אותן שורות שהטבלה הייתה מקבלת — אחרי סינון, חיפוש ומיון —
   * והבחירה המרובה, הייצוא, שורת הפעולות והמצבים הריקים נשארים
   * הקוד שכבר קיים כאן. אין מסלול נתונים שני, ולכן אין מקום שבו
   * פעולה יכולה להתקיים בטבלה ולא בכרטיס.
   *
   * לוח שלא סיפק renderCard מקבל את הטבלה בכל רוחב, כמו קודם.
   */
  renderCard?: (row: Row<T>, card: { selectionMode: boolean }) => ReactNode;
  cardLayout?: 'flat' | 'boxed';
  /**
   * תוכן שיושב מעל הרשימה בתצוגת הכרטיסים בלבד — הפעולה הראשית של
   * הלוח (ר' components/ui/Fab). גולל יחד עם הרשימה, לא נשאר מקובע.
   */
  cardListHeader?: ReactNode;
  /**
   * מסתיר את כותרת הלוח בתצוגת הכרטיסים.
   *
   * ⚠️ נועד למקרה אחד: כשסרגל הטאבים מוצג, הטאב הפעיל נושא את שם
   * הלוח — וכותרת נוספת מתחתיו היא אותה מילה פעמיים. הלוח לא מחליט
   * זאת בעצמו; העמוד שואל את useBoardTabs ומעביר את התשובה, כדי
   * ששני הרכיבים לא יגיעו למסקנות שונות.
   */
  hideTitleOnCards?: boolean;
  /**
   * תוכן שיוצב **מעל** ה-chrome של הלוח (כותרת, חיפוש, סינון) בתצוגת
   * הכרטיסים בלבד — למשל כרטיסי סיכום ופעולה ראשית שהמשתמש צריך לראות
   * לפני שהוא מחפש. בטבלה לא מרונדר: שם הלוח מסדר את החומרים בעצמו.
   */
  cardChromeTop?: ReactNode;
}

/* Figma «Group Folder»: a 40px band, whatever the row density is. */
const GROUP_ROW_HEIGHT_PX = 40;
/* One line of explanation under a header — an empty group, a search with no hits, a note. */
const GROUP_MESSAGE_ROW_HEIGHT_PX = 44;

/* הפניה יציבה, כדי שנפילה למערך ריק לא תייצר data חדש בכל render (HAT-1343). */
const EMPTY_ROWS: never[] = [];

type BodyItem<T> =
  | { kind: 'row'; row: Row<T> }
  | { kind: 'group'; group: RowGroup<T> }
  | { kind: 'message'; group: RowGroup<T>; text: ReactNode; tone: 'empty' | 'note' };

function DataTable<T>({
  columns,
  data: rawData,
  getRowId,
  rowGroups,
  isLoading,
  sortBy,
  filters,
  toolbar,
  headerActions,
  titleCaption,
  title,
  handleRowClick,
  selectionActions,
  selectionSummary,
  activeRecord,
  tableHeight,
  scrollMode = 'element',
  mobileLayout = 'table',
  minTableWidth = 0,
  customGlobalSearch,
  exportColumns,
  exportData,
  exportFilename,
  getExportData,
  onExport,
  exportPdf,
  exportExtraItems,
  skeletonConfig,
  searchQuery,
  emptyStateContent,
  setSearchQuery,
  onLoadMore,
  hasMore,
  isLoadingMore,
  loadingMoreText,
  manualSorting = false,
  onSortingChange,
  density = LEGACY_TABLE_DENSITY,
  onDensityChange,
  showCalendarMode = false,
  twoLineRows = false,
  notice,
  columnLayoutKey,
  searchPlaceholder,
  searchTooltip,
  renderCard,
  cardLayout = 'flat',
  cardListHeader,
  hideTitleOnCards,
  cardChromeTop,
}: DataTableProps<T>) {
  /*
   * HAT-1343: `data` מוקלד כמערך, אבל בזמן ריצה הוא מגיע מ-react-query ולכן הוא
   * בדיוק מה שהשרת החזיר. תשובת JSON שנקטעה באמצע חוזרת מ-axios כמחרוזת עם
   * סטטוס 200 (ר' assertJsonBody ב-ApiCalls), היא נכנסת ל-cache, וכל render
   * שלאחר מכן נפל כאן על `data.map is not a function` — באמצע render, כלומר
   * היישר ל-ErrorBoundary הגלובלי שמחליף את **כל** האפליקציה במסך שגיאה.
   *
   * ה-cache שורד את ה-remount, ולכן המסך קרס שוב ושוב ולא היתה שום דרך לצאת
   * מזה בלי טעינה מלאה. לוח אחד עם נתון פגום צריך להציג מצב ריק, לא להפיל את
   * המערכת: נופלים למערך ריק ומשאירים console.error כדי שהתקלה עדיין תגיע
   * לטלמטריה ולדיווחי הווידג'ט.
   */
  const data = useMemo(() => {
    if (Array.isArray(rawData)) return rawData;
    console.error('DataTable: expected an array for `data`, got', typeof rawData, rawData);
    return EMPTY_ROWS;
  }, [rawData]);
  const { isSidebarDocked, isMobile, isPhone: isPhoneWidth } = useDeviceDetection();
  const [calendarMode, setCalendarMode] = useCalendarMode();
  const isPhone = !isSidebarDocked;
  const documentScroll = scrollMode === 'document';
  const deferredSearchQuery = useDeferredValue(searchQuery ?? '');
  const searchFilterPending = (searchQuery ?? '') !== deferredSearchQuery;
  /*
   * HAT-1492: the fund bar, board title, filters and column header always stay on screen
   * while the rows scroll - what the thumbtack used to switch on, now with no thumbtack.
   * Only with a docked sidebar: below that, cards scroll their own list and a phone's
   * three-column board needs all of its short height for rows.
   */
  const stickyChrome = documentScroll && !isPhone;
  const [chromeHeight, setChromeHeight] = useState(0);
  const [balanceBarHeight, setBalanceBarHeight] = useState(0);
  const [scrollMargin, setScrollMargin] = useState(0);
  const [focusedRow, setFocusedRow] = useState<number | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  /*
   * ⚠️ הסף כאן הוא isMobile (מתחת ל-1100) ולא isPhone (מתחת ל-768),
   * ובכוונה — אלה שתי שאלות שונות:
   *
   *   isPhone שואל "האם הסרגל הצדדי הוא מגירה", ולפיו הטבלה מחליטה
   *   כמה עמודות להסתיר. הוא נשאר כפי שהוא לכל מי שממשיך לקבל טבלה.
   *
   *   isCardView שואל "האם יש כאן מקום לטבלה בכלל". טאבלט בפורטרט
   *   הוא 768–1024, ואחרי מסילת הסרגל נשארים ללוח כ-690 פיקסלים —
   *   שש עמודות שם נדחסות עד שכל אחת מהן היא שלוש נקודות. המשימה
   *   מבקשת את העיצוב הזה לניידים **ולטאבלטים**, וזו הנקודה שבה
   *   טאבלט נכלל.
   *
   * אין כאן סתירה בין שני הדגלים: בטווח 768–1099 הטבלה לא מרונדרת
   * כלל, ולכן ה-isPhone שבתוכה לא מוכרע בכיוון אחר — הוא פשוט לא
   * נשאל.
   *
   * HAT-1205 / HAT-1217: בטלפון (≤767) לוח התנועות הוא טבלה תלת-עמודתית
   * (תאריך | פירוט | סכום) עם זברה על כל רוחב השורה ועמודות max-content.
   * כרטיסי HAT-1170 נשארים בטאבלט 768–1099.
   */
  const isPhoneTransactionsBoard = mobileLayout === 'transactions' && isPhoneWidth;
  const isCardView = isMobile && Boolean(renderCard) && !isPhoneTransactionsBoard;
  /*
   * מצב בחירה מרובה בכרטיסים (HAT-1170).
   *
   * ⚠️ תיבת הסימון לא מוצגת דרך קבע. בעיצוב שממנו מעבירים אין
   * תיבות בכלל, ותיבה על כל כרטיס היא הפרט הראשון שהעין נופלת עליו
   * במקום השם. אבל הבחירה המרובה מזינה פעולות קיימות של האתר —
   * שליחת קבלות, ייצוא, העברה לחודש הבא — ולכן היא לא נעלמת: היא
   * נפתחת מכפתור "בחירה מרובה", ונשארת פתוחה כל עוד יש שורה מסומנת.
   */
  const [selectionMode, setSelectionMode] = useState(false);
  const chromeRef = useRef<HTMLDivElement>(null);
  const filtersSlotRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Below the docked width only a page-scrolled board offers these: a board with its own
  // scrollbar falls back to the legacy row height there, so a density pick would do nothing.
  const showDensityOption = Boolean(onDensityChange) && (!isPhone || documentScroll);
  const showCalendarOption = showCalendarMode && (!isPhone || documentScroll);
  const boardToolbar =
    onDensityChange || searchPlaceholder || toolbar || showCalendarMode ? (
      <>
        {(showDensityOption || showCalendarOption) && (
          <ViewOptionsMenu
            density={showDensityOption && onDensityChange ? { value: density, onChange: onDensityChange } : undefined}
            calendar={showCalendarOption ? { value: calendarMode, onChange: setCalendarMode } : undefined}
          />
        )}
        {searchPlaceholder && (
          <SearchInput
            value={searchQuery ?? ''}
            onChange={setSearchQuery}
            placeholder={searchPlaceholder}
            tooltipText={searchTooltip}
            filterPending={searchFilterPending}
            className={documentScroll ? styles.documentSearch : 'w-[220px] h-[32px] max-md:w-full max-md:min-w-0 max-md:max-w-full'}
          />
        )}
        {toolbar}
      </>
    ) : null;
  const crowded = useCrowdedToolbar(Boolean(boardToolbar) && !isPhone, chromeRef, filtersSlotRef, toolbarRef);
  // `airy` is a desktop-only preset (HAT-1189). A user who picks it on a big
  // screen and switches to their phone should see the two-option mobile scale
  // rather than the roomy airy row heights.
  const effectiveDensity: TableDensity = isPhone ? (documentScroll ? (density === 'airy' ? 'normal' : density) : LEGACY_TABLE_DENSITY) : density;
  // Feeds both the virtualizer and the --row-h the stylesheet reads.
  // When the two drifted apart rows overlapped, so clicks missed the <tr> and the
  // additional-info blade treated it as an outside-click and closed.
  const densityTokens = TABLE_DENSITY_TOKENS[effectiveDensity];
  const rowHeightPx = isPhone
    ? documentScroll
      ? isPhoneTransactionsBoard
        ? MOBILE_TRANSACTION_ROW_HEIGHT_PX[effectiveDensity]
        : MOBILE_DENSITY_ROW_HEIGHT_PX[effectiveDensity]
      : MOBILE_ROW_HEIGHT_PX
    : twoLineRows
      ? TABLE_DENSITY_TWO_LINE_ROW_H[effectiveDensity]
      : densityTokens.rowH;

  const getColumnClassName = useCallback(
    (meta: ColumnMeta | undefined) => {
      if (isPhone && meta?.mobileClassName) {
        return `${styles.hasMobileWidth} ${meta.mobileClassName}`;
      }
      return meta?.className || '';
    },
    [isPhone]
  );

  // Filter out columns marked as hiddenOnMobile when on mobile
  const visibleColumns = useMemo(() => {
    if (!isPhone) return columns;
    const mobileColumns = columns.filter((col) => !(col.meta as ColumnMeta)?.hiddenOnMobile);
    if (mobileLayout === 'transactions') {
      const order = ['date', 'contactName', 'amount'];
      mobileColumns.sort((a, b) => order.indexOf(a.id ?? '') - order.indexOf(b.id ?? ''));
    }
    return mobileColumns;
  }, [columns, isPhone, mobileLayout]);

  const columnIds = useMemo(
    () => visibleColumns.map((col, index) => col.id ?? (col as { accessorKey?: string }).accessorKey ?? String(index)),
    [visibleColumns]
  );

  // On a phone the columns are percentage-wide and there is nothing to grab, so
  // an arrangement built on a desktop must not follow the user there.
  const isColumnLayoutEnabled = Boolean(columnLayoutKey) && !isPhone;
  const { sizes, order, setSizes, setOrder, reset, isCustomised } = useColumnLayout(isColumnLayoutEnabled ? columnLayoutKey : undefined, columnIds);

  const theadRef = useRef<HTMLTableSectionElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // `widths` is the user's layout as saved (or as being dragged). What the table
  // actually renders is `renderedWidths`, below - the same numbers fitted to the screen.
  const [widths, setWidths] = useState<Record<string, number>>({});

  useEffect(() => {
    setWidths(isColumnLayoutEnabled ? sizes : {});
  }, [sizes, isColumnLayoutEnabled]);

  /*
   * HAT-1364: the width the board has for its columns, kept current as the window is
   * zoomed or resized and as the sidebar opens. A saved layout is a set of absolute
   * pixel widths measured on whatever screen the user had then; it is fitted to this
   * width before it is rendered, or its trailing columns fall off the screen.
   */
  const [availableWidth, setAvailableWidth] = useState(0);
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !isColumnLayoutEnabled) return;
    const measure = () => setAvailableWidth(Math.round(wrapper.getBoundingClientRect().width));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [isColumnLayoutEnabled]);

  const fitColumns = useMemo<FitColumn[]>(() => {
    const byId = new Map(visibleColumns.map((col, index) => [columnIds[index], col]));
    return order.map((id) => {
      const meta = byId.get(id)?.meta as ColumnMeta | undefined;
      return {
        id,
        flexible: meta?.flexible ?? isFlexibleColumnClass(meta?.className),
        minWidthPx: meta?.minLayoutWidthPx ?? declaredMinWidthPx(meta?.className) ?? MIN_COLUMN_WIDTH_PX,
      };
    });
  }, [visibleColumns, columnIds, order]);

  const renderedWidths = useMemo(() => fitColumnWidths(widths, fitColumns, availableWidth), [widths, fitColumns, availableWidth]);
  const renderedWidthsRef = useRef(renderedWidths);
  renderedWidthsRef.current = renderedWidths;

  // Dragging one edge must not reflow the columns beside it, so the first drag
  // freezes every column at the width it currently renders at and works from there.
  const measureColumns = useCallback(() => {
    const measured: Record<string, number> = {};
    theadRef.current?.querySelectorAll<HTMLElement>('th[data-column-id]').forEach((th) => {
      measured[th.dataset.columnId!] = Math.round(th.getBoundingClientRect().width);
    });
    return measured;
  }, []);

  // A drag owns the document until the pointer is released. Held here so it can
  // also be torn down if the board unmounts first - otherwise the listeners and
  // the col-resize cursor outlive the table they belong to.
  const endResizeRef = useRef<(() => void) | null>(null);
  useEffect(() => () => endResizeRef.current?.(), []);

  const startResize = useCallback(
    (columnId: string, event: React.PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const handle = event.currentTarget;
      // The rendered (fitted) widths, not the saved ones: the handle sits where the
      // column is drawn, and a drag that starts from a width the user cannot see would
      // not move anything until it has made up the difference.
      const base = { ...measureColumns(), ...renderedWidthsRef.current };
      const startWidth = base[columnId];
      if (!startWidth) return;
      const startX = event.clientX;
      const towardsEnd = document.dir === 'rtl' ? -1 : 1;

      const onMove = (moveEvent: PointerEvent) => {
        const next = Math.max(MIN_COLUMN_WIDTH_PX, Math.round(startWidth + (moveEvent.clientX - startX) * towardsEnd));
        setWidths({ ...base, [columnId]: next });
      };
      // pointercancel matters on touch: the browser can take the gesture away
      // (a scroll wins it) and then no pointerup ever arrives.
      const finish = () => {
        endResizeRef.current = null;
        handle.removeAttribute('data-active');
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', finish);
        document.removeEventListener('pointercancel', finish);
        document.body.classList.remove(styles.resizing);
        setSizes(renderedWidthsRef.current);
      };

      endResizeRef.current = finish;
      handle.setAttribute('data-active', 'true');
      document.body.classList.add(styles.resizing);
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', finish);
      document.addEventListener('pointercancel', finish);
    },
    [measureColumns, setSizes]
  );

  /** Same width change as a drag, for people who do not use a pointer. */
  const nudgeWidth = useCallback(
    (columnId: string, deltaPx: number) => {
      const base = { ...measureColumns(), ...renderedWidthsRef.current };
      const current = base[columnId];
      if (!current) return;
      const next = { ...base, [columnId]: Math.max(MIN_COLUMN_WIDTH_PX, current + deltaPx) };
      setWidths(next);
      setSizes(next);
    },
    [measureColumns, setSizes]
  );

  const draggedColumnRef = useRef<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  const moveColumn = useCallback(
    (fromId: string, toId: string) => {
      if (fromId === toId) return;
      const next = [...order];
      next.splice(next.indexOf(toId), 0, ...next.splice(next.indexOf(fromId), 1));
      setOrder(next);
    },
    [order, setOrder]
  );

  // The loading rows are drawn from the column definitions rather than from the
  // table, so they need the user's order applied to them separately or the
  // columns would visibly jump when the real rows arrive.
  const skeletonColumns = useMemo(() => {
    if (!isColumnLayoutEnabled) return visibleColumns;
    const byId = new Map(visibleColumns.map((col, index) => [columnIds[index], col]));
    return order.map((id) => byId.get(id)).filter((col): col is ColumnDef<T> => Boolean(col));
  }, [visibleColumns, columnIds, order, isColumnLayoutEnabled]);

  // A saved arrangement can be missing a column the board has gained since. Left
  // half-specified, the table hands the unclaimed width back to every column and
  // overwrites what the user set, so the newcomer gets measured and pinned too.
  useEffect(() => {
    if (!isColumnLayoutEnabled || Object.keys(widths).length === 0) return;
    const missing = order.filter((id) => !widths[id]);
    if (missing.length === 0) return;
    const measured = measureColumns();
    if (missing.some((id) => !measured[id])) return;
    setWidths((current) => ({ ...measured, ...current }));
  }, [isColumnLayoutEnabled, widths, order, measureColumns]);

  const totalWidth = useMemo(() => {
    const entries = order.map((id) => renderedWidths[id]);
    if (entries.some((width) => !width)) return 0;
    return entries.reduce((sum, width) => sum + width, 0);
  }, [order, renderedWidths]);

  // Left to itself, a fixed table hands any width the columns do not claim back
  // to all of them proportionally - narrowing one column would quietly widen the
  // rest and overwrite what the user set. A trailing column with no width of its
  // own swallows the slack instead, and collapses to nothing once the columns
  // outgrow the screen.
  const scrollWidth = isPhone ? 0 : Math.max(totalWidth, minTableWidth);
  const hasSlackColumn = totalWidth > 0;
  const columnCount = visibleColumns.length + (hasSlackColumn ? 1 : 0);

  const [sorting, setSorting] = useState<SortingState>(sortBy ? [sortBy] : []);
  const { t } = useTranslation();

  // Ref to track last selected row index for shift-click range selection
  const lastSelectedRowIndexRef = useRef<number | null>(null);

  // Notify parent when sorting changes (for server-side sorting)
  useEffect(() => {
    onSortingChange?.(sorting);
  }, [sorting, onSortingChange]);

  const table = useReactTable<T>({
    data,
    columns: visibleColumns,
    ...(getRowId ? { getRowId } : {}),
    filterFns: {
      fuzzy: customGlobalSearch,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    // Only apply client-side sorting if not using manual (server-side) sorting
    ...(manualSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: customGlobalSearch || 'fuzzy',
    manualSorting, // Tell react-table that sorting is handled externally
    enableMultiSort: false, // Clicking a new column replaces the current sort instead of adding to it
    state: {
      sorting,
      globalFilter: deferredSearchQuery,
      ...(isColumnLayoutEnabled ? { columnOrder: order } : {}),
    },
    onGlobalFilterChange: setSearchQuery,
    meta: {
      lastSelectedRowIndexRef, // For shift-click range selection
    },
  });

  const tableContainerRef = useRef<HTMLTableSectionElement>(null); // Ref for the scrollable element

  const { rows } = table.getRowModel();

  /*
   * HAT-1251: a getRowId that repeats is not only a rendering problem.
   *
   * The rows are keyed by their virtual slot now, so duplicates can no longer leave a
   * stale row painted over its neighbour - but two rows still share one identity, which
   * means selecting either selects both and the selection state cannot tell them apart.
   * That is a board-level mistake in the id, so it is reported where it can be fixed
   * rather than shipped in silence.
   */
  useEffect(() => {
    if (!import.meta.env.DEV || !getRowId || rows.length === 0) return;
    const seen = new Set<string>();
    for (const row of rows) {
      if (seen.has(row.id)) {
        console.error(`DataTable: getRowId returned a duplicate row id (${row.id}). Selection cannot distinguish rows that share an id.`);
        return;
      }
      seen.add(row.id);
    }
  }, [rows, getRowId]);

  /*
   * HAT-1212: לפני התיקון, כל שינוי בהפניה של `data` איפס את הבחירה.
   * ל-react-query יש טבע לחזור בהפניה חדשה על כל refetch (חלון-פוקוס,
   * polling, invalidate) גם כשהתוכן זהה — וכך נמחקה בשקט הבחירה של
   * המשתמש. עכשיו מנקים רק כשהזהויות המסודרות של השורות באמת השתנו:
   * החלפת קרן, טווח, מחיקה, או מיון (הסדר משתנה → id אחר בפאזל
   * הראשון/אחרון/אמצע).
   *
   * ⚠️ בלי getRowId, ה-signature נופל למספר-שורות בלבד, כי אז אין לנו
   * מזהה יציב להשוואה, וסיכומי טווח שבמקרה כן זהים באורך אבל שונים
   * בתוכן היו נראים לנו זהים. הפתרון האמיתי הוא ש-pages יעבירו
   * getRowId (transactionId, contactId, correlationId — כולם קיימים).
   */
  const dataSignature = useMemo(() => {
    if (!data || data.length === 0) return 'empty';
    if (!getRowId) return `L${data.length}`;
    // O(n) join once per data reference change. n is typically < 5000 and
    // this only runs when the react-query reference actually changes.
    return data.map((row, index) => getRowId(row, index)).join('|');
  }, [data, getRowId]);
  const previousSignatureRef = useRef(dataSignature);
  useEffect(() => {
    if (previousSignatureRef.current === dataSignature) return;
    previousSignatureRef.current = dataSignature;
    table.toggleAllRowsSelected(false);
  }, [dataSignature, table]);

  // Absolute-positioned rows + a single in-flow spacer of totalSize.
  // Do NOT mix this with paddingTop/paddingBottom spacer rows (that's the
  // in-flow virtualization pattern) — mixing both shrinks the scroll range
  // and misaligns click targets.
  // Do NOT measureElement: CSS already locks row height; measuring fought it
  // and produced ResizeObserver loops ($exception in PostHog).
  // Both hooks are called consistently. Only the active scroll owner subscribes.
  /*
   * HAT-1203: what the virtualizer scrolls through. Without groups it is the rows
   * themselves; with groups, each header is an item too, and the rows are re-bucketed
   * under their header **after** sorting — so a column sort orders rows inside a group
   * and never interleaves «עתידיות» with «שוטפות». Card and phone layouts have no
   * design for group headers yet, so they see the flat list.
   */
  const bodyItems = useMemo<BodyItem<T>[]>(() => {
    if (!rowGroups || rowGroups.length === 0 || isCardView || isPhoneTransactionsBoard) {
      return rows.map((row) => ({ kind: 'row', row }));
    }
    // Nothing in any group and nothing on its way: the board is empty, and it says so with
    // the same empty state it always had — not with a stack of headers reading (0).
    if (rows.length === 0 && rowGroups.every((group) => group.count === 0 && !group.loading)) return [];

    const buckets = rowGroups.map(() => [] as Row<T>[]);
    const unclaimed: Row<T>[] = [];
    for (const row of rows) {
      const index = rowGroups.findIndex((group) => group.contains(row.original));
      (index === -1 ? unclaimed : buckets[index]).push(row);
    }
    const items: BodyItem<T>[] = [];
    rowGroups.forEach((group, index) => {
      items.push({ kind: 'group', group });
      if (group.note) items.push({ kind: 'message', group, text: group.note, tone: 'note' });
      if (group.collapsed) return;
      if (buckets[index].length === 0 && !group.loading) {
        // A header followed by nothing reads as broken; say what the nothing means.
        const text = group.count === 0 ? group.emptyMessage : group.noMatchMessage;
        if (text) items.push({ kind: 'message', group, text, tone: 'empty' });
      }
      buckets[index].forEach((row) => items.push({ kind: 'row', row }));
    });
    unclaimed.forEach((row) => items.push({ kind: 'row', row }));
    return items;
  }, [rows, rowGroups, isCardView, isPhoneTransactionsBoard]);

  // HAT-1399: the same rows, in the same buckets, as the body above - so the PDF holds what is on screen.
  const pdfSections = (): PdfSection<T>[] => {
    if (!rowGroups || rowGroups.length === 0 || isCardView || isPhoneTransactionsBoard) {
      return [{ rows: rows.map((row) => row.original) }];
    }
    const sections: PdfSection<T>[] = rowGroups.map((group) => ({ label: group.label, rows: [] }));
    const unclaimed: T[] = [];
    for (const row of rows) {
      const index = rowGroups.findIndex((group) => group.contains(row.original));
      (index === -1 ? unclaimed : sections[index].rows).push(row.original);
    }
    return [...sections, { rows: unclaimed }];
  };
  const itemHeightPx = (index: number) => {
    const kind = bodyItems[index]?.kind;
    if (kind === 'group') return GROUP_ROW_HEIGHT_PX;
    if (kind === 'message') return GROUP_MESSAGE_ROW_HEIGHT_PX;
    return rowHeightPx;
  };

  const elementVirtualizer = useVirtualizer({
    count: bodyItems.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: itemHeightPx,
    overscan: 8,
    enabled: !documentScroll,
  });
  const windowVirtualizer = useWindowVirtualizer({
    count: bodyItems.length,
    estimateSize: itemHeightPx,
    overscan: 8,
    scrollMargin,
    rangeExtractor: (range) => {
      const indexes = defaultRangeExtractor(range);
      return focusedRow !== null && focusedRow < bodyItems.length ? [...new Set([...indexes, focusedRow])].sort((a, b) => a - b) : indexes;
    },
    enabled: documentScroll,
  });
  const rowVirtualizer = documentScroll ? windowVirtualizer : elementVirtualizer;

  useLayoutEffect(() => {
    if (!documentScroll) return;
    const measure = () => {
      const body = tableContainerRef.current;
      if (body) setScrollMargin(body.getBoundingClientRect().top + window.scrollY);
      if (chromeRef.current) setChromeHeight(chromeRef.current.offsetHeight);
      const balanceBar = document.querySelector<HTMLElement>('[data-fund-balance-bar]');
      // Only the sticky stack needs this height: thead sits below the bar + board chrome.
      setBalanceBarHeight(stickyChrome && balanceBar ? Math.round(balanceBar.getBoundingClientRect().height) : 0);
    };
    measure();
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    // Includes changes above the table: balance loading, filters, wrapping, density.
    observer.observe(document.body);
    if (chromeRef.current) observer.observe(chromeRef.current);
    const balanceBar = document.querySelector('[data-fund-balance-bar]');
    if (balanceBar) observer.observe(balanceBar);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
    };
  }, [documentScroll, stickyChrome]);

  // Sizes are cached by index, so a group header moving to another index (a toggle,
  // a filter) must clear them or a 40px band inherits a row's height.
  useEffect(() => {
    rowVirtualizer.measure();
  }, [rowHeightPx, rowVirtualizer, bodyItems]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  /*
   * וירטואליזציה של הכרטיסים.
   *
   * ⚠️ נפרדת מזו של הטבלה, ו**מודדת** במקום להניח גובה קבוע. זו לא
   * חזרה על הטעות שהתיעוד למעלה מזהיר מפניה: שם המדידה נלחמה ב-CSS
   * שנועל את גובה השורה, וכאן אין גובה נעול להילחם בו. להפך — הכותרת
   * ושורת המטא של הכרטיס נשברות שורה בכוונה (ר' CardList.module.scss),
   * כלומר גובה משתנה הוא מה שהעיצוב מבקש, ומדידה היא הדרך היחידה
   * להציב אותו נכון.
   */
  const cardScrollRef = useRef<HTMLDivElement>(null);
  /*
   * HAT-1170 — שלוש דרגות: בטלפון עמודה אחת; בטאבלט (768+) כרטיסי-מיכל
   * (בקשות תמיכה) בשתי עמודות. שורה שטוחה (תנועות, הוראות קבע) נשארת
   * עמודה אחת בכל רוחב — שתי עמודות של שורות מופרדות בקו נקראות כטבלה
   * שבורה. הווירטואלייזר תומך בזה ישירות דרך lanes: כל פריט מקבל lane
   * ומוצב לפי אחוז רוחב, והמדידה ממשיכה לעבוד לכל כרטיס בנפרד.
   */
  const cardLanes = cardLayout === 'boxed' && !isPhoneWidth ? 2 : 1;
  const cardVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => cardScrollRef.current,
    // הערכה ראשונית בלבד; המדידה מחליפה אותה בפריים הראשון. שני
    // המבנים נבדלים בגובה הבסיס — לכרטיס במיכל יש ריפוד ושוליים.
    estimateSize: () => (cardLayout === 'boxed' ? 96 : 62),
    measureElement: (element) => element.getBoundingClientRect().height,
    overscan: 6,
    lanes: cardLanes,
  });

  // מספר העמודות משתנה עם הרוחב; בלי מדידה מחדש הפריטים נשארים
  // בהיסטים של הפריסה הקודמת.
  useEffect(() => {
    cardVirtualizer.measure();
  }, [cardLanes, cardVirtualizer]);
  const virtualCards = cardVirtualizer.getVirtualItems();
  const cardsTotalSize = cardVirtualizer.getTotalSize();

  const virtualOffset = documentScroll ? scrollMargin : 0;
  // Endless scrolling for the table: element-scroll or document-scroll, whichever scrollMode picked.
  useEffect(() => {
    const body = tableContainerRef.current;
    if (isCardView || !body || !onLoadMore || !hasMore || isLoadingMore) return;
    const scrollTarget = documentScroll ? window : body;
    const handleScroll = () => {
      const remaining = documentScroll ? body.getBoundingClientRect().bottom - window.innerHeight : body.scrollHeight - body.scrollTop - body.clientHeight;
      if (remaining < 200) onLoadMore();
    };
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollTarget.removeEventListener('scroll', handleScroll);
  }, [documentScroll, onLoadMore, hasMore, isLoadingMore, isCardView]);

  // Endless scrolling for the card view (HAT-1170) — its own scrollport
  // (cardScrollRef), bound only while cards are actually on screen.
  useEffect(() => {
    const scrollContainer = cardScrollRef.current;
    if (!isCardView || !scrollContainer || !onLoadMore || !hasMore || isLoadingMore) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // Trigger when user is within 200px of the bottom
      if (scrollHeight - scrollTop - clientHeight < 200) {
        onLoadMore();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [onLoadMore, hasMore, isLoadingMore, isCardView]);

  useEffect(() => {
    if (!scrollWidth && tableRef.current) tableRef.current.style.translate = '';
  }, [scrollWidth]);

  const chromeClassName = [styles.boardChrome, filters ? styles.hasFilters : '', notice ? styles.hasNotice : '', boardToolbar ? styles.hasToolbar : '']
    .filter(Boolean)
    .join(' ');

  /*
   * סרגל הבחירה המרובה — מחולץ כדי ששתי התצוגות יציגו את **אותו**
   * סרגל. עותק שני היה הדרך הקצרה ביותר לכך שפעולת בחירה תעבוד
   * בטבלה ותיעדר בכרטיסים, וזה בדיוק מה שהמסך הזה לא אמור לעשות.
   */
  const selectedRows = table.getSelectedRowModel().rows;
  const isSelecting = selectionMode || selectedRows.length > 0;
  const selectionBarContent = selectedRows.length > 0 && (
    <div className="flex justify-between">
      <div className="flex gap-6 items-center">
        <span className="body-md-semibold text-(--font-body-4)">
          {selectedRows.length} {t('selectedRecords')}
        </span>
        <span className="body-md-semibold text-(--font-body-4)">|</span>
        <button className="body-md-link text-(--font-body-4)" onClick={() => table.toggleAllRowsSelected(false)}>
          {t('cancelSelection')}
        </button>
        {selectionSummary && selectionSummary(selectedRows.map((row) => row.original))}
      </div>
      <div className="flex gap-4 pe-3">
        {selectionActions!.map(({ label, onClick, isVisible, disabled, style, className }, index) => {
          const selectedItems = selectedRows.map((row) => row.original);

          return (
            (!isVisible || isVisible(selectedItems)) && (
              <button
                key={index}
                className={`${styles['selection-action']} body-sm-semibold ${className || ''}`}
                style={style}
                disabled={disabled}
                onClick={() => onClick(selectedItems)}
              >
                {label}
              </button>
            )
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      ref={wrapperRef}
      className={`${styles.tableContainer} ${documentScroll ? styles.documentScroll : ''} ${mobileLayout === 'transactions' ? styles.transactionMobile : ''}`}
      data-scroll-mode={scrollMode}
      data-sticky-chrome={stickyChrome}
      data-card-board={isCardView ? 'true' : undefined}
      style={
        {
          '--sticky-balance-height': `${stickyChrome ? balanceBarHeight : 0}px`,
          '--sticky-chrome-height': `${stickyChrome ? balanceBarHeight + chromeHeight : 0}px`,
        } as React.CSSProperties
      }
    >
      {isCardView && cardChromeTop}
      <div ref={chromeRef} className={`${chromeClassName} ${stickyChrome ? styles.stickyChrome : ''}`} data-crowded={crowded ? 'true' : 'false'}>
        {(!(isCardView && hideTitleOnCards) || titleCaption) && (
          <div className={styles.tableHeaderTitleBlock}>
            {!(isCardView && hideTitleOnCards) && <h1 className={styles.tableHeaderTitle}>{title}</h1>}
            {titleCaption && <div className={styles.tableHeaderCaption}>{titleCaption}</div>}
          </div>
        )}
        <div className={styles.tableHeaderActions}>
          {headerActions}
          {isColumnLayoutEnabled && isCustomised && (
            <button type="button" className={`${styles.resetLayout} body-sm-regular`} onClick={reset}>
              {t('resetColumnLayout')}
            </button>
          )}
          {exportColumns && (
            <ExportTableBtn
              data={exportData || data}
              columns={exportColumns}
              filename={exportFilename || title}
              getData={getExportData}
              onExport={onExport}
              onExportPdf={exportPdf && (() => exportPdf(pdfSections()))}
              extraItems={exportExtraItems}
              compact={documentScroll && isPhone}
            />
          )}
          {isCardView && selectionActions && selectionActions.length > 0 && (
            <button
              type="button"
              className={`${styles.resetLayout} body-sm-regular`}
              aria-pressed={isSelecting}
              onClick={() => {
                if (isSelecting) {
                  table.toggleAllRowsSelected(false);
                  setSelectionMode(false);
                } else {
                  setSelectionMode(true);
                }
              }}
            >
              {isSelecting ? t('cancelSelection') : t('multiSelect')}
            </button>
          )}
        </div>
        {boardToolbar && (
          <div ref={toolbarRef} className={styles.toolbar}>
            {boardToolbar}
          </div>
        )}
        {notice && <div className={styles.noticeSlot}>{notice}</div>}
        {filters && (
          <div ref={filtersSlotRef} className={styles.filtersSlot}>
            {filters}
          </div>
        )}
      </div>
      {isCardView ? (
        <>
          <div ref={cardScrollRef} className={`${styles.cardScroll} custom-scrollbar ph-mask`}>
            {/* מעל הרשימה, וגם כשהיא ריקה — הפעולה הראשית היא בדיוק
                מה שרוצים כשאין עדיין מה להציג. */}
            {cardListHeader}
            {isLoading ? (
              /* אותו שלד כמו בטבלה, בצורת כרטיס: כמה שורות אפורות
                 בגובה משוער. skeletonConfig מתאר עמודות ואין לו
                 משמעות כאן, ולכן הוא לא נקרא בענף הזה. */
              <div className={styles.cardSkeletons}>
                {Array.from({ length: 8 }).map((_, index) => (
                  <div key={`card-skeleton-${index}`} className={styles.cardSkeleton}>
                    <Skeleton variant="circular" width={34} height={34} sx={{ bgcolor: 'var(--surface-grey-lighter)' }} />
                    <div className={styles.cardSkeletonLines}>
                      <Skeleton variant="text" width={index % 2 === 0 ? '68%' : '44%'} height={14} sx={{ bgcolor: 'var(--surface-grey-lighter)' }} />
                      <Skeleton variant="text" width="36%" height={10} sx={{ bgcolor: 'var(--surface-grey-lighter)' }} />
                    </div>
                    <Skeleton variant="text" width={62} height={14} sx={{ bgcolor: 'var(--surface-grey-lighter)' }} />
                  </div>
                ))}
              </div>
            ) : rows.length === 0 ? (
              <div className={styles['emptyStateContainer']}>
                <img src={emptyState} alt="" />
                <span className="heading-h3-semibold pt-6">{emptyStateContent?.title}</span>
                <span className="body-lr-regular text-(--font-body-3)">{emptyStateContent?.description}</span>
                {emptyStateContent?.action && <div className="pt-4">{emptyStateContent.action}</div>}
              </div>
            ) : (
              <CardList layout={cardLayout}>
                {/* מרווח יחיד בגובה הכולל, והכרטיסים ממוקמים מוחלטות
                    מעליו — אותה תבנית וירטואליזציה של הטבלה. */}
                <div className={styles.cardVirtualSpacer} style={{ height: `${cardsTotalSize}px` }}>
                  {virtualCards.map((virtualCard) => {
                    const row = rows[virtualCard.index];
                    return (
                      <div
                        // המפתח הוא המשבצת הווירטואלית ולא row.id, מאותה סיבה
                        // שבשורות הטבלה (HAT-1251): getRowId שאינו ייחודי משאיר
                        // כרטיס ישן תקוע במקומו הקודם.
                        key={virtualCard.key}
                        // ⚠️ ref המדידה חייב לשבת על העוטף שנושא את המיקום,
                        // ולא על הכרטיס שבתוכו: הווירטואלייזר מזהה את
                        // הפריט לפי data-index שעליו.
                        ref={cardVirtualizer.measureElement}
                        data-index={virtualCard.index}
                        className={`${styles.cardSlot} ${cardLanes > 1 ? styles.cardSlotLane : ''}`}
                        style={{
                          transform: `translateY(${virtualCard.start}px)`,
                          insetInlineStart: `${(virtualCard.lane / cardLanes) * 100}%`,
                          width: `${100 / cardLanes}%`,
                        }}
                      >
                        {renderCard!(row, { selectionMode: isSelecting })}
                      </div>
                    );
                  })}
                </div>
              </CardList>
            )}

            {isLoadingMore && (
              <div className={styles.loadingMoreContainer}>
                <div className={styles.loadingSpinner}></div>
                <span className="body-md-regular">{loadingMoreText || t('loadingMoreRecords')}</span>
              </div>
            )}
            {/* In-flow spacer: padding-bottom on this overflow flex item does not
                reliably extend scrollHeight, so the last card sat under the FAB. */}
            <div className={styles.cardFabClearance} aria-hidden="true" />
          </div>
          {/* מחוץ לאזור הגלילה: סרגל הבחירה נשאר גלוי בזמן שגוללים,
              אחרת הוא נעלם בדיוק כשבוחרים שורות שדורשות גלילה. */}
          {selectionBarContent && <div className={`${styles.cardSelectionBar} bg-(--surface-secondary-dark)`}>{selectionBarContent}</div>}
        </>
      ) : (
        <>
          {/* A native horizontal strip moves the real header and body together without
          making an overflow ancestor that would capture vertical sticky positioning.
          HAT-1364: element-scrolling boards use it too. Their own scrollbar sat at the
          foot of a container that is taller than the viewport by design, i.e. below the
          fold, so a table that had outgrown the screen looked simply cut off. */}
          {scrollWidth > 0 && (
            <div
              className={styles.horizontalScroll}
              tabIndex={0}
              role="region"
              aria-label={t('scrollTableHorizontally')}
              onScroll={(event) => {
                if (tableRef.current) tableRef.current.style.translate = `${-event.currentTarget.scrollLeft}px 0`;
              }}
            >
              <div style={{ width: `${scrollWidth}px`, height: '1px' }} />
            </div>
          )}
          <div className={`${styles.tableScroll} ${totalWidth && !documentScroll ? styles.scrollsSideways : ''} custom-scrollbar`}>
            <table
              ref={tableRef}
              aria-label={title}
              onFocusCapture={(event) => {
                const row = event.target.closest<HTMLElement>('[data-index]');
                setFocusedRow(row ? Number(row.dataset.index) : null);
              }}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusedRow(null);
              }}
              aria-rowcount={bodyItems.length + 1}
              className={`${styles.table} data-table custom-scrollbar`}
              data-density={effectiveDensity}
              style={
                {
                  ...densityCssVars(densityTokens, rowHeightPx),
                  ...(isPhoneTransactionsBoard ? phoneTransactionBoardCssVars(effectiveDensity) : {}),
                  ...(scrollWidth ? { minWidth: `${scrollWidth}px` } : {}),
                } as React.CSSProperties
              }
            >
              <thead ref={theadRef} className={styles.thead}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const meta = header.column.columnDef.meta as ColumnMeta | undefined;
                      const columnId = header.column.id;
                      const canArrange = isColumnLayoutEnabled && !meta?.lockLayout;
                      return (
                        <th
                          key={header.id}
                          scope="col"
                          aria-sort={header.column.getIsSorted() === 'asc' ? 'ascending' : header.column.getIsSorted() === 'desc' ? 'descending' : undefined}
                          data-column-id={columnId}
                          className={`${styles.th} ${canArrange ? styles.thArrangeable : ''} ${getColumnClassName(meta)} ${dropTargetId === columnId ? styles.dropTarget : ''}`}
                          style={{ ...meta?.style, ...columnWidthStyle(renderedWidths[columnId], meta?.minLayoutWidthPx) }}
                          draggable={canArrange}
                          onDragStart={(event) => {
                            draggedColumnRef.current = columnId;
                            event.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragOver={(event) => {
                            if (!canArrange || !draggedColumnRef.current || draggedColumnRef.current === columnId) return;
                            event.preventDefault();
                            setDropTargetId(columnId);
                          }}
                          onDragLeave={() => setDropTargetId((current) => (current === columnId ? null : current))}
                          onDrop={(event) => {
                            event.preventDefault();
                            if (draggedColumnRef.current) moveColumn(draggedColumnRef.current, columnId);
                            draggedColumnRef.current = null;
                            setDropTargetId(null);
                          }}
                          onDragEnd={() => {
                            draggedColumnRef.current = null;
                            setDropTargetId(null);
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canArrange && (
                            <span
                              className={styles.resizeHandle}
                              role="separator"
                              aria-orientation="vertical"
                              aria-label={t('resizeColumn')}
                              title={t('resizeColumn')}
                              tabIndex={0}
                              onPointerDown={(event) => startResize(columnId, event)}
                              onClick={(event) => event.stopPropagation()}
                              onKeyDown={(event) => {
                                // Arrows follow what the eye sees, so the same key
                                // widens the column in Hebrew and in English.
                                const towardsEnd = document.dir === 'rtl' ? -1 : 1;
                                const step = event.key === 'ArrowLeft' ? -16 : event.key === 'ArrowRight' ? 16 : 0;
                                if (!step) return;
                                event.preventDefault();
                                nudgeWidth(columnId, step * towardsEnd);
                              }}
                            />
                          )}
                        </th>
                      );
                    })}
                    {hasSlackColumn && <th className={styles.th} aria-hidden="true" />}
                  </tr>
                ))}
              </thead>
              <tbody
                ref={tableContainerRef}
                className={`${styles.tbody} custom-scrollbar ph-mask`}
                style={{
                  ...(documentScroll ? {} : isPhone ? { height: 'calc(100vh - 260px)' } : tableHeight ? { height: tableHeight } : { flex: 1, minHeight: 0 }),
                  overflow: documentScroll ? 'visible' : 'auto',
                  position: 'relative',
                }}
              >
                {isLoading && (
                  <tr className={styles.virtualSpacer} aria-hidden="true">
                    <td colSpan={columnCount} style={{ height: `${20 * rowHeightPx}px` }} />
                  </tr>
                )}
                {!isLoading && bodyItems.length > 0 && (
                  <tr className={styles.virtualSpacer} aria-hidden="true">
                    <td colSpan={columnCount} style={{ height: `${Math.max(totalSize, rowHeightPx)}px` }} />
                  </tr>
                )}

                {isLoading ? (
                  Array.from({ length: Math.min(table.getState().pagination?.pageSize || 20, 20) }).map((_, index) => (
                    <tr
                      key={`skeleton-${index}`}
                      className={styles.tr}
                      {...(isPhoneTransactionsBoard ? { 'data-row-tone': index % 2 === 1 ? 'alt' : 'base' } : {})}
                      style={{
                        position: 'absolute',
                        top: 0,
                        insetInlineStart: 0,
                        width: '100%',
                        height: `${rowHeightPx}px`,
                        transform: `translateY(${index * rowHeightPx}px)`,
                      }}
                    >
                      {skeletonColumns.map((column, cellIndex) => {
                        const meta = column.meta as ColumnMeta | undefined;
                        const cellStyle = { ...meta?.style, ...columnWidthStyle(column.id ? renderedWidths[column.id] : undefined, meta?.minLayoutWidthPx) };
                        let skeletonWidths: string[] = ['70%'];
                        let skeletonHeight = '10px';
                        let skeletonVariant: 'text' | 'rectangular' | 'rounded' | 'circular' = 'text';
                        const isEvenRow = index % 2 === 0;

                        if (column.id !== undefined && skeletonConfig && column.id in skeletonConfig) {
                          const config = skeletonConfig[column.id](isEvenRow);
                          skeletonWidths = config.widths || ['70%'];

                          if (config.customRender) {
                            return (
                              <td
                                key={`skeleton-cell-${index}-${cellIndex}`}
                                data-column-id={column.id}
                                className={`${styles.td} body-md-regular ${getColumnClassName(meta)}`}
                                style={cellStyle}
                              >
                                {config.customRender()}
                              </td>
                            );
                          }

                          if (column.id === 'select') {
                            skeletonVariant = 'rectangular';
                            skeletonHeight = '16px';
                          }
                        }

                        return (
                          <td
                            key={`skeleton-cell-${index}-${cellIndex}`}
                            data-column-id={column.id}
                            className={`${styles.td} body-md-regular ${getColumnClassName(meta)}`}
                            style={cellStyle}
                          >
                            {skeletonWidths.map((_, i) => (
                              <div key={`skeleton-line-${i}`} className={i > 0 ? 'mt-[8px]' : ''}>
                                <Skeleton
                                  variant={skeletonVariant}
                                  width={skeletonWidths[i % skeletonWidths.length]}
                                  height={skeletonHeight}
                                  animation="pulse"
                                  sx={{
                                    bgcolor: 'var(--surface-grey-lighter)',
                                    ...(column.id === 'select' ? { borderRadius: '4px' } : {}),
                                  }}
                                />
                              </div>
                            ))}
                          </td>
                        );
                      })}
                      {hasSlackColumn && <td className={styles.td} aria-hidden="true" />}
                    </tr>
                  ))
                ) : bodyItems.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount}>
                      <div className={styles['emptyStateContainer']}>
                        <img src={emptyState} alt="" />

                        <span className="heading-h3-semibold pt-6">{emptyStateContent?.title}</span>

                        <span className="body-lr-regular text-(--font-body-3)">{emptyStateContent?.description}</span>
                        {emptyStateContent?.action && <div className="pt-4">{emptyStateContent.action}</div>}
                      </div>
                    </td>
                  </tr>
                ) : (
                  virtualRows.map((virtualRow) => {
                    const item = bodyItems[virtualRow.index];
                    if (item.kind === 'group') {
                      const { group } = item;
                      return (
                        <tr
                          key={virtualRow.key}
                          className={styles.groupRow}
                          style={{
                            position: 'absolute',
                            top: 0,
                            insetInlineStart: 0,
                            width: '100%',
                            height: `${GROUP_ROW_HEIGHT_PX}px`,
                            transform: `translateY(${virtualRow.start - virtualOffset}px)`,
                          }}
                          aria-rowindex={virtualRow.index + 2}
                          data-index={virtualRow.index}
                          data-group-row={group.id}
                        >
                          <td colSpan={columnCount} className={styles.groupCell}>
                            <div className={styles.groupBar}>
                              <button type="button" className={`body-lr-medium ${styles.groupToggle}`} onClick={group.onToggle} aria-expanded={!group.collapsed} data-group-toggle={group.id}>
                                <svg className={styles.groupChevron} data-collapsed={group.collapsed} viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
                                  <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span>{group.label}</span>
                                <span className={styles.groupCount} data-zero={group.count === 0 && !group.loading ? 'true' : undefined}>
                                  ({group.count.toLocaleString()})
                                </span>
                                {group.loading && <span className={styles.groupSpinner} role="status" aria-label={group.loadingLabel} title={group.loadingLabel} />}
                                {/* Figma: the dot is the closed folder's hint. Open, the flagged rows speak for themselves. */}
                                {group.flagged && group.collapsed && (
                                  <span className={styles.groupFlag} title={group.flagTitle} role={group.flagTitle ? 'img' : undefined} aria-label={group.flagTitle} aria-hidden={group.flagTitle ? undefined : true} />
                                )}
                                {group.badge && <span className={`body-xs-sm-regular ${styles.groupBadge}`}>{group.badge}</span>}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    if (item.kind === 'message') {
                      return (
                        <tr
                          key={virtualRow.key}
                          className={styles.groupMessageRow}
                          style={{
                            position: 'absolute',
                            top: 0,
                            insetInlineStart: 0,
                            width: '100%',
                            height: `${GROUP_MESSAGE_ROW_HEIGHT_PX}px`,
                            transform: `translateY(${virtualRow.start - virtualOffset}px)`,
                          }}
                          aria-rowindex={virtualRow.index + 2}
                          data-index={virtualRow.index}
                          data-group-message={item.group.id}
                        >
                          <td colSpan={columnCount} className={`body-md-regular ${styles.groupMessageCell}`} data-tone={item.tone}>
                            {item.text}
                          </td>
                        </tr>
                      );
                    }
                    const { row } = item;
                    return (
                      <tr
                        /*
                         * HAT-1251: the key is the virtual slot, NOT row.id.
                         *
                         * A board whose getRowId is not unique (the transactions board keyed
                         * rows by transactionId, and one transaction carries several detail
                         * rows) hands React the same key twice. React then reconciles the
                         * duplicates against each other, leaves the loser mounted with the
                         * transform it had before, and two rows paint on top of each other -
                         * which is what "השורות עולות אחת על השניה" was. The virtual index is
                         * one key per painted slot by construction, so no data can collide it.
                         * Row identity for selection still comes from getRowId, in the table.
                         */
                        key={virtualRow.key}
                        className={`${styles.tr} ${handleRowClick ? 'cursor-pointer' : ''}`}
                        onClick={() => handleRowClick && handleRowClick(row.original)}
                        style={{
                          position: 'absolute',
                          top: 0,
                          insetInlineStart: 0,
                          width: '100%',
                          height: `${rowHeightPx}px`,
                          transform: `translateY(${virtualRow.start - virtualOffset}px)`,
                        }}
                        aria-rowindex={virtualRow.index + 2}
                        data-index={virtualRow.index}
                        data-table-row="true"
                        {...(isPhoneTransactionsBoard ? { 'data-row-tone': virtualRow.index % 2 === 1 ? 'alt' : 'base' } : {})}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            data-column-id={cell.column.id}
                            className={`${styles.td} ${row.getIsSelected() ? styles.selected : ''} ${row.original === activeRecord ? styles.active : ''} ${getColumnClassName(cell.column.columnDef.meta as ColumnMeta)}`}
                            style={{
                              ...(cell.column.columnDef.meta as ColumnMeta)?.style,
                              ...columnWidthStyle(renderedWidths[cell.column.id], (cell.column.columnDef.meta as ColumnMeta)?.minLayoutWidthPx),
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                        {hasSlackColumn && (
                          <td
                            className={`${styles.td} ${row.getIsSelected() ? styles.selected : ''} ${row.original === activeRecord ? styles.active : ''}`}
                            aria-hidden="true"
                          />
                        )}
                      </tr>
                    );
                  })
                )}

                {isLoadingMore && (
                  <tr className={styles.loadingMoreTr} style={{ transform: `translateY(${totalSize}px)` }}>
                    <td colSpan={columnCount} className={styles.loadingMoreRow}>
                      <div className={styles.loadingMoreContainer} role="status">
                        <div className={styles.loadingSpinner}></div>
                        <span className="body-md-regular">{loadingMoreText || t('loadingMoreRecords')}</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className={styles.tfoot}>
                {selectionBarContent && (
                  <tr>
                    <td colSpan={columnCount} className={`${styles.td} bg-(--surface-secondary-dark)`}>
                      {selectionBarContent}
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default DataTable;
